/**
 * Checkbox — la case à cocher, dans la recette de « ce qu'on tape »
 * (champs.css) : une case creusée sous une hairline ; cochée, elle prend la
 * marque, luit un peu, et la coche (bold) s'y pose d'un petit bond. Le texte
 * reste blanc ou gris, jamais la marque (la langue validée par Kayla).
 * Retapée au lot 1 de la refonte du catalogue (28-10-2026) : plus grande
 * (18 px par défaut), un texte lisible (13 px), une aide sous le label.
 *
 * **Pourquoi ce composant et pas un `<input type="checkbox">` brut ?**
 * Les checkboxes natives + `accent-*` rendent mal sur dark theme (le check
 * est sale, le contour est moche), et chaque panel finissait avec une couleur
 * d'accent différente. Cette primitive unifie le look produit. Une règle
 * ESLint `no-restricted-syntax` bloque `type="checkbox"` sur `<input>` dans
 * tout le cockpit (sauf ce fichier).
 *
 * Variantes :
 *   - row    : case + label inline (clic sur tout = bascule), `aide` dessous.
 *   - inline : juste la case (pas de container) — quand l'intégrateur
 *              câble son propre <label>/<button> autour (cas TagsEditor,
 *              FlowPanel picker, TeamPanel perms, etc.).
 *
 * Tailles : xs 14 px (listes denses) · sm 18 px (défaut) · md 22 px.
 *
 * 🩸 **Les deux conteneurs sont `relative`, et c'est un CORRECTIF, pas du
 * style.** Le vrai `<input>` est en `sr-only` — donc `position: absolute`.
 * Sans conteneur positionné, son bloc contenant est le premier ancêtre
 * positionné, et dans le cockpit c'est `.cockpit-panel-body` : la case d'une
 * liste longue se retrouve posée à des milliers de pixels sous le panneau.
 *
 * Cliquer le label FOCUSE cet input, et le navigateur le fait défiler jusqu'à
 * l'écran — en scrollant le seul conteneur qui peut l'atteindre, le corps du
 * panneau. `overflow: hidden` ne l'en empêche pas : il cache la barre, il ne
 * retire pas le scroll. Mesuré le 25-08-2026 dans le harnais de l'app Audio,
 * sur la 32ᵉ carte : `cockpit-panel-body.scrollTop` 0 → **2119**, l'en-tête du
 * panneau projeté à −2070, la barre d'action à −892. Kayla : « quand je
 * sélectionne des trucs, la partie du bas elle remonte […] pourquoi j'ai un
 * énorme vide comme ça ? »
 *
 * Avec `relative`, l'input reste dans la carte : le navigateur défile la
 * GRILLE (le bon conteneur) et le panneau ne bouge pas. Vérifié dans le même
 * harnais : `corps` 0, `grille` 2273, en-tête à sa place. Le test
 * `__tests__/Checkbox.ancrage.test.tsx` le garde.
 */
import type { ReactNode } from "react";

import { Check } from "./icons";
import { cn } from "./utils";
import "./champs.css";

export type CheckboxSize = "xs" | "sm" | "md";

export interface CheckboxProps {
  checked: boolean;
  onChange?: (next: boolean) => void;
  /** Si fourni, rend un container `<label>` clickable (variante `row`). */
  label?: ReactNode;
  /** Sous le label, en gris (variante `row`). */
  aide?: ReactNode;
  disabled?: boolean;
  /** Empeche la propagation `onChange` — utile quand le parent gere le
   *  click (variante "inline" dans un `<button>` parent). */
  readOnly?: boolean;
  size?: CheckboxSize;
  className?: string;
  /** Aria-label si pas de `label` visible. */
  ariaLabel?: string;
  id?: string;
}

const CASE: Record<CheckboxSize, { boite: string; arrondi: string; coche: number; label: string; aide: string }> = {
  xs: { boite: "h-3.5 w-3.5", arrondi: "ch-case-xs", coche: 9, label: "text-[12px]", aide: "text-[11px]" },
  sm: { boite: "h-[18px] w-[18px]", arrondi: "ch-case", coche: 12, label: "text-[13px]", aide: "text-[11.5px]" },
  md: { boite: "h-[22px] w-[22px]", arrondi: "ch-case", coche: 15, label: "text-[14px]", aide: "text-[12px]" },
};

export function Checkbox({
  checked,
  onChange,
  label,
  aide,
  disabled,
  readOnly,
  size = "sm",
  className,
  ariaLabel,
  id,
}: CheckboxProps) {
  const t = CASE[size];
  const input = (
    <input
      type="checkbox"
      checked={checked}
      onChange={readOnly ? undefined : (e) => onChange?.(e.target.checked)}
      disabled={disabled}
      readOnly={readOnly}
      aria-label={ariaLabel}
      id={id}
      className="sr-only"
    />
  );
  const box = <CheckboxBox checked={checked} disabled={disabled} size={size} ariaLabel={ariaLabel} id={id} />;

  // Variante row : container <label> avec texte.
  if (label !== undefined) {
    return (
      <label
        className={cn(
          // 🩸 `relative` n'est PAS décoratif — cf le pavé en tête de fichier.
          "ch-coche relative flex cursor-pointer items-start gap-2.5 text-fg",
          t.label,
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        {input}
        <span className={cn("flex shrink-0 items-center", size === "xs" ? "h-[1.3em]" : "h-[1.4em]")}>{box}</span>
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="leading-snug">{label}</span>
          {aide !== undefined && <span className={cn("leading-snug text-faint", t.aide)}>{aide}</span>}
        </span>
      </label>
    );
  }

  // Variante inline : juste la case (parent gere le click).
  return (
    <span className={cn("ch-coche relative inline-flex", className)}>
      {input}
      {box}
    </span>
  );
}

function CheckboxBox({
  checked,
  disabled,
  size,
  ariaLabel,
  id,
}: {
  checked: boolean;
  disabled?: boolean;
  size: CheckboxSize;
  ariaLabel?: string;
  id?: string;
}) {
  const t = CASE[size];
  return (
    <span
      aria-hidden={ariaLabel ? undefined : true}
      aria-label={ariaLabel}
      data-checked={checked || undefined}
      data-coche={checked || undefined}
      data-disabled={disabled || undefined}
      data-for={id}
      className={cn("ch-case", t.arrondi, t.boite, disabled && "opacity-50")}
    >
      {checked && <Check size={t.coche} weight="bold" aria-hidden />}
    </span>
  );
}
